import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { getRelatedContent } from '../../config/contentRegistry';
import {
  School as SchoolIcon,
  Calculate as CalculateIcon,
  SportsFootball as SportsFootballIcon,
  SportsBasketball as SportsBasketballIcon,
  SportsBaseball as SportsBaseballIcon,
  SportsHockey as SportsHockeyIcon
} from '@mui/icons-material';

/**
 * RelatedContent Component
 * 
 * Automatically displays related content based on category, sport, and tags.
 * Used in guides, hub pages, and market pages.
 * 
 * Props:
 * - category: 'quick-start' | 'strategies' | 'sports' | null
 * - sport: 'nfl' | 'nba' | 'mlb' | 'nhl' | 'ncaaf' | 'mens-cbb' | null
 * - tags: Array of tags for matching
 * - currentPath: Path to exclude from results
 * - title: Optional custom title (default: "Continue Learning")
 * - limit: Number of items to show (default: 4)
 */
const RelatedContent = ({
  category = null,
  sport = null,
  tags = [],
  currentPath = null,
  title = 'Continue Learning',
  limit = 4
}) => {
  const relatedItems = getRelatedContent(
    { category, sport, tags, excludePath: currentPath },
    limit
  );

  if (relatedItems.length === 0) {
    return null;
  }

  const getIcon = (item) => {
    if (item.category === 'tools') {
      return <CalculateIcon sx={{ color: 'primary.main', mr: 1.5 }} />;
    }
    if (item.category === 'sports' || item.sport) {
      const sportIcons = {
        nfl: SportsFootballIcon,
        nba: SportsBasketballIcon,
        mlb: SportsBaseballIcon,
        nhl: SportsHockeyIcon,
        ncaaf: SportsFootballIcon,
        'mens-cbb': SportsBasketballIcon
      };
      const Icon = sportIcons[item.sport] || SchoolIcon;
      return <Icon sx={{ color: 'primary.main', mr: 1.5 }} />;
    }
    return <SchoolIcon sx={{ color: 'primary.main', mr: 1.5 }} />;
  };

  return (
    <Box sx={{ mt: 5, mb: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 700 }}>
        {title}
      </Typography>
      <Grid container spacing={3}>
        {relatedItems.map((item) => {
          const Icon = getIcon(item);
          return (
            <Grid item xs={12} sm={6} md={3} key={item.path}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    {Icon}
                    <Typography variant="h6" component="h4" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {item.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      flexGrow: 1,
                      color: 'text.secondary',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {item.description || item.title}
                  </Typography>
                  <Button
                    component={Link}
                    to={item.path}
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    sx={{ mt: 'auto' }}
                  >
                    {item.category === 'tools' ? 'Use Tool' : 'Read More'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default RelatedContent;
